/**
 * Peel system_settings for Redux auth bootstrap: stash every row under
 * {@link SYSTEM_SETTINGS_WIRE_KEY}, keep only legacy globals in `settings`.
 */
export const SYSTEM_SETTINGS_WIRE_KEY = '__systemSettingsWire' as const;

export type SystemSettingWireRow = {
  name: string;
  setting: string;
  settings_type: string;
  value: unknown;
  enterprise_workspace_id: number | null;
  enterprise_team_id: number | null;
};

export type AuthSettingsResultWithWire = {
  settings?: Record<string, unknown>;
  system_settings_all?: SystemSettingWireRow[];
  [SYSTEM_SETTINGS_WIRE_KEY]?: SystemSettingWireRow[];
  [key: string]: unknown;
};

function isWireRow(value: unknown): value is SystemSettingWireRow {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.name === 'string';
}

function normalizeWireRow(row: SystemSettingWireRow): SystemSettingWireRow {
  return {
    name: row.name,
    setting: String(row.setting ?? ''),
    settings_type: String(row.settings_type ?? 'string'),
    value: row.value,
    enterprise_workspace_id: row.enterprise_workspace_id ?? null,
    enterprise_team_id: row.enterprise_team_id ?? null
  };
}

export function isGlobalSystemSettingRow(row: SystemSettingWireRow): boolean {
  return row.enterprise_workspace_id == null && row.enterprise_team_id == null;
}

export function toSystemSettingsWire(rows: unknown): SystemSettingWireRow[] {
  if (!Array.isArray(rows)) return [];
  return rows.filter(isWireRow).map(normalizeWireRow);
}

export function globalsOnlySettingsMap(
  wire: SystemSettingWireRow[]
): Record<string, SystemSettingWireRow> {
  return Object.fromEntries(
    wire.filter(isGlobalSystemSettingRow).map(row => [row.name, row])
  );
}

/**
 * Transform GET /settings auth payload: globals-only `settings` for Redux,
 * full row list on {@link SYSTEM_SETTINGS_WIRE_KEY} for SystemSettingsService seed.
 */
export function peelSystemSettingsFromAuthResult(
  result: AuthSettingsResultWithWire
): AuthSettingsResultWithWire & {
  [SYSTEM_SETTINGS_WIRE_KEY]: SystemSettingWireRow[];
} {
  const fromAll = toSystemSettingsWire(result.system_settings_all);
  const fromMap =
    fromAll.length === 0 &&
    result.settings &&
    typeof result.settings === 'object'
      ? toSystemSettingsWire(Object.values(result.settings))
      : [];
  const wire = fromAll.length > 0 ? fromAll : fromMap;
  const { system_settings_all: _drop, ...rest } = result;

  return {
    ...rest,
    settings: globalsOnlySettingsMap(wire),
    [SYSTEM_SETTINGS_WIRE_KEY]: wire
  } as AuthSettingsResultWithWire & {
    [SYSTEM_SETTINGS_WIRE_KEY]: SystemSettingWireRow[];
  };
}
